// Success is reported only after the persistence promise resolves.
export async function persistOrder({ id, data, onAdd, onUpdate }) {
  if (id) {
    await onUpdate(id, data);
    return id;
  }
  const savedId = await onAdd(data);
  if (!savedId) throw new Error('The order was not saved. Please sign in and try again.');
  return savedId;
}

export const readLocalRecords = (key, fallback) => {
  const stored = localStorage.getItem(key);
  if (stored == null) return fallback;
  const records = JSON.parse(stored);
  if (!Array.isArray(records)) throw new Error('Invalid local records. Do not overwrite existing data.');
  return records;
};

// Keep the form until all media is saved. A failed upload retries the SAME order ID,
// and successful uploads are cached so retrying does not upload those files twice.
export async function saveOrderWithUploads({ id, data, screenshots = [], itemFiles = [],
  upload, cache = new Map(), onAdd, onUpdate, onSaved, onProgress = () => {} }) {
  const queue = [
    ...screenshots.filter(shot => shot.file).map(shot => ({ file: shot.file, type: 'screenshot' })),
    ...itemFiles.map((file, index) => ({ file, index, type: 'item' })).filter(entry => entry.file)
  ];
  const retained = screenshots.filter(shot => !shot.file && shot.url).map(shot => shot.url);
  const payload = { ...data, screenshots: retained, mediaUploadStatus: queue.length ? 'pending' : 'complete' };
  const docId = await persistOrder({ id, data: payload, onAdd, onUpdate });
  onSaved(docId);
  if (!queue.length) return docId;

  let finished = 0;
  const results = await Promise.allSettled(queue.map(async entry => {
    const url = cache.get(entry.file) || await upload(entry.file);
    if (!url) throw new Error('Photo upload did not return a URL.');
    cache.set(entry.file, url);
    onProgress(++finished, queue.length);
    return { ...entry, url };
  }));
  const items = (data.items || []).map(item => ({ ...item }));
  const urls = [...retained];
  results.forEach(result => {
    if (result.status !== 'fulfilled') return;
    if (result.value.type === 'screenshot') urls.push(result.value.url);
    else items[result.value.index].referenceImage = result.value.url;
  });
  const failed = results.some(result => result.status === 'rejected');
  await onUpdate(docId, { items, screenshots: urls, mediaUploadStatus: failed ? 'failed' : 'complete' });
  if (failed) throw new Error('Order saved, but some photos failed. Retry to finish uploading.');
  return docId;
}
